import { useEffect, useState } from "react";
import { Radar, RefreshCw, Send, X, Settings, AlertTriangle, ExternalLink, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const LABEL_STYLE = {
  aderente: "bg-emerald-50 text-emerald-700 border-emerald-200",
  possivel: "bg-amber-50 text-amber-700 border-amber-200",
  pesquisa: "bg-sky-50 text-sky-700 border-sky-200",
};
const LABEL_TEXT = { aderente: "Aderente", possivel: "Possível", pesquisa: "Em pesquisa" };

function ResearchNote({ t }) {
  if (t.qual_status !== "em_pesquisa") return null;
  return (
    <div className="mt-3 rounded-xl border border-sky-100 bg-sky-50/60 p-3" data-testid={`tender-research-${t.id}`}>
      <p className="text-sm text-sky-900"><span className="font-semibold">Por que está em pesquisa:</span> {t.gate_reason}</p>
      {t.pendencias?.length > 0 && (
        <p className="mt-1 text-xs text-sky-800"><span className="font-semibold">Pendências:</span> {t.pendencias.join("; ")}</p>
      )}
      <p className="mt-1 text-[11px] text-sky-700/80">Não é enviado no WhatsApp até cumprir todos os filtros.</p>
    </div>
  );
}

function Bullet({ label, value }) {
  if (!value) return null;
  const v = Array.isArray(value) ? value.join("; ") : (typeof value === "object" ? Object.values(value).filter(Boolean).join("; ") : value);
  if (!v) return null;
  return (
    <p className="text-sm text-slate-600"><span className="font-semibold text-slate-800">{label}:</span> {v}</p>
  );
}

function TenderCard({ t, onSend, onDismiss, onDelete, sending }) {
  const loc = [t.municipio, t.uf].filter(Boolean).join("/") || t.orgao_nome;
  const b = t.brief || {};
  return (
    <div data-testid={`tender-card-${t.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${LABEL_STYLE[t.aderencia_label] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {LABEL_TEXT[t.aderencia_label] || "Possível"}
            </span>
            {t.estagio && (
              <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600" data-testid={`tender-stage-${t.id}`}>{t.estagio}</span>
            )}
            {t.urgency === "imediato" && (
              <span className="flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                <AlertTriangle size={12} /> URGENTE
              </span>
            )}
            {!t.gate_version && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-500" title="Encontrada antes das regras estritas (valor oficial, escopo integral, documentos). Não é reavaliada." data-testid={`tender-legacy-${t.id}`}>Regras antigas</span>
            )}
            {t.status === "updated" && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-600">Atualizado</span>}
            {t.status === "sent" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Enviado</span>}
            {t.data_encerramento && !isNaN(Date.parse(t.data_encerramento)) && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600" data-testid={`tender-deadline-${t.id}`}>
                📅 Encerra {new Date(t.data_encerramento).toLocaleDateString("pt-BR")}
              </span>
            )}
          </div>
          <h3 className="mt-2 font-semibold text-slate-900">{loc} — {t.modalidade || "Licitação"} {t.numero || ""}</h3>
          <p className="text-xs text-slate-400">{t.orgao_nome}</p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          {t.qual_status !== "em_pesquisa" && <button data-testid={`tender-send-${t.id}`} onClick={() => onSend(t)} disabled={sending}
            title="Enviar ao comercial" className="rounded-full bg-[#0b1220] p-2 text-white transition-opacity hover:opacity-90 disabled:opacity-50">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>}
          <button data-testid={`tender-dismiss-${t.id}`} onClick={() => onDismiss(t)}
            title="Descartar" className="rounded-full border border-slate-200 p-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600">
            <X size={15} />
          </button>
          <button data-testid={`tender-delete-${t.id}`} onClick={() => onDelete(t)}
            title="Excluir" className="rounded-full border border-slate-200 p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-700">{b.o_que || t.objeto}</p>
      <ResearchNote t={t} />
      <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
        <Bullet label="Por que interessa" value={b.por_que_adere} />
        {t.valor_estimado > 0 && (
          <Bullet label={t.strict?.unidade?.base === "lote" ? `Valor oficial do lote (${t.strict.unidade.nome})` : "Valor oficial (PNCP)"} value={`R$ ${Number(t.valor_estimado).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
        )}
        {!(t.valor_estimado > 0) && <Bullet label="Potencial" value={b.potencial} />}
        <Bullet label="Prazo" value={b.prazo} />
        <Bullet label="O que pode impedir" value={b.impeditivos} />
        <Bullet label="Pegadinhas" value={b.pegadinhas} />
        <Bullet label="Exigências" value={b.exigencias} />
        <Bullet label="Divergências (confirmar)" value={b.divergencias} />
        <Bullet label="Ação imediata" value={b.acao_agora} />
      </div>
      {t.link_pncp && (
        <a href={t.link_pncp} target="_blank" rel="noreferrer" data-testid={`tender-link-${t.id}`}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:underline">
          Ver no PNCP <ExternalLink size={13} />
        </a>
      )}
    </div>
  );
}

function SettingsDrawer({ open, onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/tenders/settings").then(({ data }) => setS(data)).catch(() => {}); }, [open]);
  if (!open) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenders/settings", {
        enabled: s.enabled, profile: s.profile, digest_hour: Number(s.digest_hour),
        terms: (s.terms || []), min_value: Number(s.min_value) || 0,
      });
      toast.success("Ajustes salvos.");
      onClose();
    } catch (e) { toast.error("Não foi possível salvar (apenas gestores)."); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div data-testid="tender-settings-drawer" className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Ajustes do Radar</h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {!s ? <p className="mt-6 text-sm text-slate-400">Carregando…</p> : (
          <div className="mt-5 space-y-5">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="tender-enabled" type="checkbox" checked={!!s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
              Radar ativo (monitorar automaticamente)
            </label>
            <div>
              <label className="text-sm font-medium text-slate-700">Perfil de aderência (o que a Isa procura)</label>
              <textarea data-testid="tender-profile" value={s.profile || ""} onChange={(e) => setS({ ...s, profile: e.target.value })}
                rows={10} className="mt-1 w-full rounded-xl border border-slate-200 p-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Valor mínimo do edital</label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-slate-500">R$</span>
                <input data-testid="tender-min-value" type="text" inputMode="numeric"
                  value={Number(s.min_value || 0).toLocaleString("pt-BR")}
                  onChange={(e) => setS({ ...s, min_value: Number(e.target.value.replace(/\D/g, "")) || 0 })}
                  className="w-40 rounded-xl border border-slate-200 p-2 text-sm" />
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Piso do valor estimado OFICIAL (mínimo R$ 600 mil; lote independente: R$ 900 mil). Valor sigiloso ou não divulgado fica em pesquisa, sem alerta.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Hora do digest diário</label>
              <input data-testid="tender-digest-hour" type="number" min={0} max={23} value={s.digest_hour ?? 8}
                onChange={(e) => setS({ ...s, digest_hour: e.target.value })} className="mt-1 w-24 rounded-xl border border-slate-200 p-2 text-sm" />
            </div>
            <button data-testid="tender-save-settings" onClick={save} disabled={saving}
              className="w-full rounded-full bg-[#0b1220] py-2.5 text-sm font-medium text-white disabled:opacity-50">
              {saving ? "Salvando…" : "Salvar ajustes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TendersRadar({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("urgencia");
  const [deleting, setDeleting] = useState(null);

  const load = (f = filter) => {
    setLoading(true);
    api.get("/tenders", { params: f === "pesquisa" ? { label: "pesquisa" } : {} }).then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Não foi possível carregar as licitações."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/tenders/scan");
      if (data?.running) { toast.info("Já existe uma varredura em andamento. Aguarde alguns minutos."); return; }
      toast.success("Varredura iniciada. Leva alguns minutos (PNCP, PCA, DOU e diários + leitura dos editais). Atualize a lista em instantes.");
      setTimeout(() => load(), 60000);
    } catch (e) { toast.error("A varredura demorou ou falhou. Tente novamente."); }
    finally { setScanning(false); }
  };

  const send = async (t) => {
    setSendingId(t.id);
    try { const { data } = await api.post(`/tenders/${t.id}/send`); toast.success(`Enviado a ${data.sent_to} do comercial.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível enviar."); }
    finally { setSendingId(null); }
  };
  const dismiss = async (t) => {
    try { await api.post(`/tenders/${t.id}/dismiss`); toast.success("Descartada."); setItems((p) => p.filter((x) => x.id !== t.id)); }
    catch (e) { toast.error("Não foi possível descartar."); }
  };
  const [delBusy, setDelBusy] = useState(false);
  const confirmDelete = async () => {
    if (!deleting) return;
    setDelBusy(true);
    try {
      await api.delete(`/tenders/${deleting.id}`);
      toast.success("Licitação excluída.");
      setItems((p) => p.filter((x) => x.id !== deleting.id));
      setDeleting(null);
    } catch (e) { toast.error("Não foi possível excluir."); }
    finally { setDelBusy(false); }
  };

  const visible = items.filter((t) => t.status !== "dismissed" && (filter === "urgente" ? t.urgency === "imediato" : filter === "possivel" ? t.aderencia_label === "possivel" : true));
  const _pd = (s) => { const v = Date.parse(s); return isNaN(v) ? null : v; };
  const sorted = [...visible].sort((a, b) => {
    if (sort === "prazo") {
      const da = _pd(a.data_encerramento), db = _pd(b.data_encerramento);
      if (da == null && db == null) return 0;
      if (da == null) return 1; if (db == null) return -1;
      return da - db;
    }
    if (sort === "recentes") return (_pd(b.data_publicacao) || 0) - (_pd(a.data_publicacao) || 0);
    // urgência (padrão): urgentes primeiro, depois aderentes antes de possíveis, prazo como desempate
    const ua = a.urgency === "imediato" ? 0 : 1, ub = b.urgency === "imediato" ? 0 : 1;
    if (ua !== ub) return ua - ub;
    const la = a.aderencia_label === "aderente" ? 0 : 1, lb = b.aderencia_label === "aderente" ? 0 : 1;
    if (la !== lb) return la - lb;
    return (_pd(a.data_encerramento) || Infinity) - (_pd(b.data_encerramento) || Infinity);
  });

  return (
    <div data-testid="tenders-radar-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {!embedded && <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Radar size={24} /> Radar de Licitações</h1>}
          <p className="text-sm text-slate-500">A Isa varre PNCP, PCA, DOU e diários municipais e só avisa no WhatsApp o que tiver valor oficial ≥ R$ 600 mil, escopo integral e documentos validados.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="tender-scan-btn" onClick={scan} disabled={scanning}
            className="flex items-center gap-2 rounded-full bg-[#0b1220] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50">
            {scanning ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {scanning ? "Buscando…" : "Buscar agora"}
          </button>
          <button data-testid="tender-settings-btn" onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Settings size={15} /> Ajustes
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {[["", "Todas"], ["urgente", "Urgentes"], ["possivel", "Possíveis"], ["pesquisa", "Em pesquisa"]].map(([k, lbl]) => (
            <button key={k} data-testid={`tender-filter-${k || "todas"}`} onClick={() => setFilter(k)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-[#0b1220] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-500">
          Ordenar por
          <select data-testid="tender-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100">
            <option value="urgencia">Urgência</option>
            <option value="prazo">Prazo (encerra antes)</option>
            <option value="recentes">Mais recentes</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div data-testid="tenders-empty" className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-16 text-center">
          <Radar className="mx-auto text-slate-300" size={40} />
          <p className="mt-3 text-slate-500">Nenhuma oportunidade ainda. Clique em <span className="font-medium">Buscar agora</span> para varrer o PNCP.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {sorted.map((t) => <TenderCard key={t.id} t={t} onSend={send} onDismiss={dismiss} onDelete={setDeleting} sending={sendingId === t.id} />)}
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-900/40 p-4" onClick={() => setDeleting(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="tender-delete-modal">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Excluir licitação</h3>
              <button onClick={() => setDeleting(null)} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-600">
              Excluir definitivamente <b>{[deleting.municipio, deleting.uf].filter(Boolean).join("/") || deleting.orgao_nome}</b> — {deleting.modalidade} {deleting.numero || ""}? Ela sai do radar. Esta ação não pode ser desfeita.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDeleting(null)} className="rounded-full px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button onClick={confirmDelete} disabled={delBusy} data-testid="tender-delete-confirm"
                className="rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50">
                {delBusy ? "Excluindo…" : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}

      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
